import type { ParticipantService } from '@/scopes/account/group-responsible/domain/types'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from '@/intl'
import { useGroupResponsibleStore } from '@/scopes/account/group-responsible/infrastructure/useGroupResponsibleStore'
import {
  getParticipantExtraInfo,
  getParticipantExtrasTotal,
} from '../selectors'
import {
  FieldSection,
  FieldSectionTitle,
  NoShrink,
  SectionTitle,
  SheetContentStyled,
  SheetContentTitle,
  SheetHeaderStyled,
  SpacedRow,
} from './SheetPortal'

interface ServiceFieldProps {
  label: string
  service: ParticipantService | undefined
  flagOnly?: boolean
}

function ServiceField({ label, service, flagOnly = false }: ServiceFieldProps) {
  const intl = useIntl()
  if (flagOnly && !service)
    return null

  const displayValue = flagOnly
    ? intl.formatMessage({ id: 'yes_label' })
    : (service?.description || '-')

  return (
    <FieldSection>
      <FieldSectionTitle
        variant="body-s-regular-tight"
        forwardedAs="div"
      >
        {label}
      </FieldSectionTitle>
      <SpacedRow>
        <Typography>{displayValue}</Typography>
        {service && service.price > 0 && (
          <NoShrink>{intl.formatCurrency(service.price)}</NoShrink>
        )}
      </SpacedRow>
    </FieldSection>
  )
}

export function ParticipantExtraInfoSheetPortal() {
  const participantExtraInfo = useGroupResponsibleStore(getParticipantExtraInfo)
  const extrasTotal = useGroupResponsibleStore(getParticipantExtrasTotal)
  const activeExtraInfoId = useGroupResponsibleStore(state => state.activeExtraInfoId)
  const changeExtraInfoId = useGroupResponsibleStore(state => state.changeExtraInfoId)
  const intl = useIntl()

  const isOpen = !!activeExtraInfoId

  function handleOpenChange(open: boolean) {
    if (!open) {
      changeExtraInfoId('')
    }
  }

  return (
    <Sheet.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Sheet.Body side="right">
        <SheetHeaderStyled
          closeLabel={intl.formatMessage({ id: 'close' })}
          titleLabel={intl.formatMessage({ id: 'common_participant' })}
        />
        <SheetContentStyled>
          <SheetContentTitle forwardedAs="div" variant="display-g-regular">
            {participantExtraInfo.name}
          </SheetContentTitle>
          <FieldSection>
            <FieldSectionTitle variant="body-s-regular-tight" forwardedAs="div">
              {intl.formatMessage({ id: 'email_address' })}
            </FieldSectionTitle>
            <Typography>{participantExtraInfo.email}</Typography>
          </FieldSection>
          <FieldSection>
            <FieldSectionTitle variant="body-s-regular-tight" forwardedAs="div">
              {intl.formatMessage({ id: 'groups_responsible_booking_number' })}
            </FieldSectionTitle>
            <Typography>{participantExtraInfo.bookingNumber}</Typography>
          </FieldSection>
          <FieldSection>
            <FieldSectionTitle variant="body-s-regular-tight" forwardedAs="div">
              {intl.formatMessage({ id: 'groups_responsible_phone' })}
            </FieldSectionTitle>
            {participantExtraInfo.mobileNumber || participantExtraInfo.telephoneNumber
              ? (
                  <>
                    <Typography as="div">{participantExtraInfo.mobileNumber}</Typography>
                    <Typography as="div">{participantExtraInfo.telephoneNumber}</Typography>
                  </>
                )
              : (
                  <Typography>-</Typography>
                )}
          </FieldSection>
          <FieldSection>
            <FieldSectionTitle variant="body-s-regular-tight" forwardedAs="div">
              {intl.formatMessage({ id: 'groups_responsible_accommodation' })}
            </FieldSectionTitle>
            <Typography>{participantExtraInfo.accommodationName}</Typography>
          </FieldSection>
          <FieldSection>
            <FieldSectionTitle variant="body-s-regular-tight" forwardedAs="div">
              {intl.formatMessage({ id: 'groups_responsible_room_type' })}
            </FieldSectionTitle>
            <Typography>{participantExtraInfo.roomType}</Typography>
          </FieldSection>
          <SectionTitle variant="body-l-bold-tight" forwardedAs="div">
            {intl.formatMessage({ id: 'common_extras' })}
          </SectionTitle>
          <ServiceField label={intl.formatMessage({ id: 'common_ski_pass' })} service={participantExtraInfo.skiPass} />
          <ServiceField label={intl.formatMessage({ id: 'common_ski_material' })} service={participantExtraInfo.skiMaterial} />
          <ServiceField label={intl.formatMessage({ id: 'common_ski_lesson' })} service={participantExtraInfo.skiLesson} />
          <ServiceField label={intl.formatMessage({ id: 'groups_responsible_sheets' })} service={participantExtraInfo.sheets} flagOnly />
          <ServiceField label={intl.formatMessage({ id: 'groups_responsible_towels' })} service={participantExtraInfo.towels} flagOnly />
          <ServiceField label={intl.formatMessage({ id: 'groups_responsible_travel_insurance' })} service={participantExtraInfo.travelInsurance} />
          <ServiceField label={intl.formatMessage({ id: 'groups_responsible_cancel_insurance' })} service={participantExtraInfo.cancelInsurance} />
          <ServiceField label={intl.formatMessage({ id: 'groups_responsible_policy_costs' })} service={participantExtraInfo.policyCosts} />
          <ServiceField label={intl.formatMessage({ id: 'groups_responsible_size_of_sweater' })} service={participantExtraInfo.sweaterSize} />
          {extrasTotal.total > 0 && (
            <FieldSection>
              <SpacedRow>
                <div>
                  <Typography variant="body-l-bold-tight" as="div">
                    {intl.formatMessage({ id: 'groups_responsible_total_extras' })}
                  </Typography>
                  <FieldSectionTitle variant="body-s-regular-tight" forwardedAs="div">
                    {extrasTotal.paidServicesLabelKeys.map(key => intl.formatMessage({ id: key })).join(' · ')}
                  </FieldSectionTitle>
                </div>
                <NoShrink variant="body-l-bold-tight">{intl.formatCurrency(extrasTotal.total)}</NoShrink>
              </SpacedRow>
            </FieldSection>
          )}
        </SheetContentStyled>
      </Sheet.Body>
    </Sheet.Root>
  )
}
